import { css } from "@emotion/react"
import { List, ListItem, ListItemIcon, Typography } from "matsuri-ui"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useLanguage } from "@/providers/LanguageProvider"
import {
    SupportedLanguage,
    languageLabels,
    supportedLanguages
} from "@/utils/languageStorage"
import {
    getDownloadedLanguages,
    isTranslationAvailable
} from "@/utils/translationService"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"

export const LanguageSettingsPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { currentLanguage, setLanguage } = useLanguage()
    const [switchingLang, setSwitchingLang] =
        useState<SupportedLanguage | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [downloadedLangs, setDownloadedLangs] = useState<
        Set<SupportedLanguage>
    >(() => new Set<SupportedLanguage>(["ja"]))
    const [confirmLang, setConfirmLang] = useState<SupportedLanguage | null>(
        null
    )

    const isNative = isTranslationAvailable()

    useEffect(() => {
        getDownloadedLanguages().then(langs =>
            setDownloadedLangs(new Set(langs))
        )
    }, [])

    const handleSelect = useCallback(
        async (language: SupportedLanguage) => {
            if (language === currentLanguage || switchingLang) return
            setSwitchingLang(language)
            setError(null)
            try {
                await setLanguage(language)
                setDownloadedLangs(prev => {
                    const next = new Set(prev)
                    next.add(language)
                    return next
                })
            } catch {
                setError(t("言語の切替に失敗しました"))
            } finally {
                setSwitchingLang(null)
            }
        },
        [currentLanguage, switchingLang, setLanguage, t]
    )

    const handleLanguageClick = useCallback(
        (language: SupportedLanguage) => {
            if (language === currentLanguage || switchingLang) return
            // 日本語またはダウンロード済みなら確認なしで即切替
            if (language === "ja" || downloadedLangs.has(language)) {
                handleSelect(language)
            } else {
                // 未ダウンロードなら確認モーダルを表示
                setConfirmLang(language)
            }
        },
        [currentLanguage, switchingLang, downloadedLangs, handleSelect]
    )

    return (
        <MobileAppHeader
            title={
                <Typography variant="h3" as="h3">
                    {t("言語設定")}
                </Typography>
            }
            onPrev={() => navigate(-1)}
            style={{
                boxSizing: "border-box",
                paddingTop: "env(safe-area-inset-top, 0px)"
            }}
        >
            <div
                css={css`
                    padding: 5px 16px;
                `}
            >
                {!isNative && (
                    <div
                        css={css`
                            padding: 12px;
                            margin-bottom: 16px;
                            font-size: 14px;
                            color: ${theme.palette.gray[600]};
                            background-color: ${theme.palette.gray[100]};
                            border-radius: 8px;
                        `}
                    >
                        {t("言語切替はネイティブアプリでのみ利用可能です")}
                    </div>
                )}

                {error && (
                    <div
                        css={css`
                            padding: 12px;
                            margin-bottom: 16px;
                            font-size: 14px;
                            color: ${theme.palette.intentions.secondary.main};
                            background-color: #fef2f2;
                            border-radius: 8px;
                        `}
                    >
                        {error}
                    </div>
                )}

                <List>
                    {supportedLanguages.map(lang => {
                        const isSelected = lang === currentLanguage
                        const isSwitching = switchingLang === lang
                        const isDownloaded = downloadedLangs.has(lang)

                        return (
                            <ListItem
                                key={lang}
                                css={css`
                                    cursor: ${isNative ? "pointer" : "default"};
                                `}
                                onClick={() => {
                                    if (isNative) {
                                        handleLanguageClick(lang)
                                    }
                                }}
                            >
                                <div
                                    css={css`
                                        flex: 1;
                                        text-align: left;
                                    `}
                                >
                                    <Typography
                                        variant="body"
                                        css={css`
                                            font-weight: ${
                                                isSelected ? 600 : 400
                                            };
                                        `}
                                    >
                                        {languageLabels[lang]}
                                    </Typography>
                                    {isSwitching ? (
                                        <Typography
                                            variant="caption"
                                            css={css`
                                                color: ${
                                                    theme.palette.intentions
                                                        .primary.main
                                                };
                                            `}
                                        >
                                            {isDownloaded
                                                ? t("切替中...")
                                                : t("ダウンロード中...")}
                                        </Typography>
                                    ) : (
                                        lang !== "ja" && (
                                            <Typography
                                                variant="caption"
                                                css={css`
                                                    color: ${
                                                        theme.palette.gray[600]
                                                    };
                                                `}
                                            >
                                                {isDownloaded
                                                    ? t("ダウンロード済み")
                                                    : t("未ダウンロード")}
                                            </Typography>
                                        )
                                    )}
                                </div>

                                {isSwitching ? (
                                    <ListItemIcon>
                                        <div
                                            css={css`
                                                width: 20px;
                                                height: 20px;
                                                border: 3px solid
                                                    ${theme.palette.gray[200]};
                                                border-top-color: ${
                                                    theme.palette.intentions
                                                        .primary.main
                                                };
                                                border-radius: 50%;
                                                animation: spin 1s linear
                                                    infinite;

                                                @keyframes spin {
                                                    to {
                                                        transform: rotate(
                                                            360deg
                                                        );
                                                    }
                                                }
                                            `}
                                        />
                                    </ListItemIcon>
                                ) : (
                                    isSelected && (
                                        <ListItemIcon>
                                            <CheckCircleIcon
                                                css={css`
                                                    font-size: 20px;
                                                    color: ${
                                                        theme.palette.intentions
                                                            .primary.main
                                                    };
                                                `}
                                            />
                                        </ListItemIcon>
                                    )
                                )}
                            </ListItem>
                        )
                    })}
                </List>
            </div>

            <ConfirmDialog
                open={confirmLang !== null}
                title={t("翻訳モデルのダウンロード")}
                message={t(
                    "翻訳モデルをダウンロードします。\nよろしいですか？"
                )}
                confirmLabel={t("ダウンロード")}
                onConfirm={() => {
                    if (confirmLang) {
                        handleSelect(confirmLang)
                    }
                    setConfirmLang(null)
                }}
                onCancel={() => setConfirmLang(null)}
            />
        </MobileAppHeader>
    )
}
